import React, { useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, RefreshControl,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import pilotService, { openNavigationToMission } from '../services/pilotService';
import MissionCard from '../components/MissionCard';

const MONTH_ES = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

const todayLabel = () => {
  const d = new Date();
  return `${d.getDate()} de ${MONTH_ES[d.getMonth()]} ${d.getFullYear()}`;
};

const missionTypeIcon = (item) => {
  if (item._type === 'mission_request' && item.mr_type === 'multispectral') return 'leaf-outline';
  if (item._type === 'mission_request') return 'water-outline';
  const objs = (item.objectives || []).map(o => {
    const name = typeof o === 'string' ? o : (o.name || o.description || '');
    return typeof name === 'string' ? name.toLowerCase() : '';
  });
  if (objs.some(o => o.includes('ndvi') || o.includes('multispectral'))) return 'leaf-outline';
  if (objs.some(o => o.includes('thermal') || o.includes('t\u00e9rmico'))) return 'flame-outline';
  if (objs.some(o => o.includes('rgb') || o.includes('mapeo'))) return 'map-outline';
  if (objs.some(o => o.includes('spray') || o.includes('aplicac'))) return 'water-outline';
  return 'airplane-outline';
};

export default function PilotDashboardScreen({ navigation }) {
  const { user } = useSelector(s => s.auth);
  const [missions, setMissions]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [weather, setWeather]       = useState(null);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const data = await pilotService.getAllPilotMissions();
      setMissions(data);
      // Cargar clima de la misión activa si existe y es drone
      const active = data.find(m => m.status === 'assigned' || m.status === 'in_progress');
      if (active?.mr_type && active?._mission_id) {
        pilotService.getMissionWeather(active._mission_id)
          .then(d => { if (d) setWeather(d); })
          .catch(() => {});
      } else {
        setWeather(null);
      }
    } catch (_) {}
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const now         = new Date();
  const thisMonth   = missions.filter(m => {
    if (!m.created_at) return false;
    const d = new Date(m.created_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const completedMonth = thisMonth.filter(m => m.status === 'completed');
  const totalHa = completedMonth.reduce((acc, m) => {
    return acc + (m.field_blocks || []).reduce((s, b) => s + (parseFloat(b.area) || 0), 0);
  }, 0);

  const activeMission = missions.find(m => m.status === 'assigned' || m.status === 'in_progress');
  const firstName = user?.name?.split(' ')[0] || user?.first_name || user?.email?.split('@')[0] || 'Piloto';

  const goToDetail = (item) => navigation.navigate('PilotMissionDetail', { item });

  if (loading) {
    return <View style={styles.centered}><ActivityIndicator size="large" color="#2c3e50" /></View>;
  }

  return (
    <ScrollView
      style={styles.container}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load(true)} tintColor="#2c3e50" />}
    >
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.greeting}>Hola, {firstName} 👋</Text>
          <Text style={styles.date}>{todayLabel()}</Text>
        </View>
        <View style={styles.avatarCircle}>
          <Text style={styles.avatarText}>{firstName[0].toUpperCase()}</Text>
        </View>
      </View>

      {/* Misión activa */}
      {activeMission ? (
        <TouchableOpacity style={[styles.activeCard, activeMission.mr_type === 'multispectral' && { backgroundColor: '#065f46' }]} onPress={() => goToDetail(activeMission)} activeOpacity={0.85}>
          <View style={styles.activeCardTop}>
            <View style={styles.activeBadge}>
              <Ionicons
                name={activeMission.mr_type === 'multispectral' ? 'leaf-outline' : activeMission._type === 'mission_request' ? 'water-outline' : 'airplane-outline'}
                size={12} color="#fff" style={{ marginRight: 4 }}
              />
              <Text style={styles.activeBadgeText}>
                {activeMission.mr_type === 'multispectral' ? 'VUELO MULTIESPECTRAL' : activeMission._type === 'mission_request' ? 'APLICACIÓN AÉREA' : 'MISIÓN ACTIVA'}
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={18} color="#fff" />
          </View>

          <Text style={styles.activeTitle}>
            {activeMission.location_name || activeMission.enterprise_name || activeMission.mission_name}
          </Text>
          <Text style={styles.activeId}>
            {activeMission.mr_type === 'multispectral'
              ? `Misión #${activeMission._mission_id}`
              : activeMission._type === 'mission_request'
              ? `Orden #${String(activeMission.order_folio).padStart(4,'0')} · Misión #${activeMission._mission_id}`
              : `Misión #${activeMission.mission_sid || activeMission._raw_id}`}
          </Text>

          {/* Cuerpo según tipo */}
          {activeMission.mr_type === 'multispectral' ? (
            <>
              {(activeMission.objectives_list || []).length > 0 && (
                <Text style={styles.activeSub}>
                  🌿 {activeMission.objectives_list.join(' · ')}
                </Text>
              )}
              {activeMission.scheduled_date && (
                <Text style={styles.activeSub}>
                  📅 {new Date(activeMission.scheduled_date + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })}
                </Text>
              )}
              {weather?.today && (
                <View style={styles.activeWeatherRow}>
                  <View style={styles.activeWeatherItem}>
                    <Ionicons name="thermometer-outline" size={13} color="rgba(255,255,255,0.7)" />
                    <Text style={styles.activeWeatherText}>{weather.today.min}–{weather.today.max}°C</Text>
                  </View>
                  <View style={styles.activeWeatherItem}>
                    <Ionicons name="flag-outline" size={13} color="rgba(255,255,255,0.7)" />
                    <Text style={styles.activeWeatherText}>{weather.today.wind} km/h</Text>
                  </View>
                  {weather.today.rain > 0 && (
                    <View style={styles.activeWeatherItem}>
                      <Ionicons name="rainy-outline" size={13} color="rgba(255,255,255,0.7)" />
                      <Text style={styles.activeWeatherText}>{weather.today.rain} mm</Text>
                    </View>
                  )}
                </View>
              )}
            </>
          ) : activeMission._type === 'mission_request' ? (
            <>
              <Text style={styles.activeSub}>
                {(activeMission.field_blocks || []).map(b => b.name).join(' · ')}
              </Text>
              {(activeMission.products || []).length > 0 && (
                <Text style={styles.activeSub}>
                  {activeMission.products.map(p => {
                    const totalHa = (activeMission.field_blocks || []).reduce((s, b) => s + (parseFloat(b.area) || 0), 0);
                    const total = totalHa > 0
                      ? `${p.trade_name}: ${(p.dose_per_machine * totalHa).toFixed(1)} ${p.dose_unit}`
                      : `${p.trade_name}: ${p.dose_per_machine} ${p.dose_unit}/ha`;
                    return total;
                  }).join('  ·  ')}
                  {activeMission.rate_type === 'variable' ? '  ·  Tasa variable' : ''}
                </Text>
              )}
              {(activeMission.scheduled_date || activeMission.application_date) && (
                <Text style={styles.activeSub}>
                  📅 {new Date((activeMission.scheduled_date || activeMission.application_date) + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })}
                </Text>
              )}
              {weather?.today && (
                <View style={styles.activeWeatherRow}>
                  <View style={styles.activeWeatherItem}>
                    <Ionicons name="thermometer-outline" size={13} color="rgba(255,255,255,0.7)" />
                    <Text style={styles.activeWeatherText}>{weather.today.min}–{weather.today.max}°C</Text>
                  </View>
                  <View style={styles.activeWeatherItem}>
                    <Ionicons name="flag-outline" size={13} color="rgba(255,255,255,0.7)" />
                    <Text style={styles.activeWeatherText}>{weather.today.wind} km/h</Text>
                  </View>
                  {weather.today.rain > 0 && (
                    <View style={styles.activeWeatherItem}>
                      <Ionicons name="rainy-outline" size={13} color="rgba(255,255,255,0.7)" />
                      <Text style={styles.activeWeatherText}>{weather.today.rain} mm</Text>
                    </View>
                  )}
                </View>
              )}
            </>
          ) : (
            <Text style={styles.activeSub}>
              {activeMission.mission_description || activeMission.mission_name}
            </Text>
          )}

          {/* Botón Cómo llegar — si tiene polígono */}
          {(activeMission.location_polygon || (activeMission.field_blocks || []).some(b => b.polygon)) && (
            <TouchableOpacity
              style={styles.activeNavBtn}
              onPress={(e) => { e.stopPropagation?.(); openNavigationToMission(activeMission); }}
              activeOpacity={0.75}
            >
              <Ionicons name="navigate-outline" size={14} color="rgba(255,255,255,0.9)" />
              <Text style={styles.activeNavBtnText}>Cómo llegar</Text>
            </TouchableOpacity>
          )}
        </TouchableOpacity>
      ) : (
        <View style={styles.noActiveCard}>
          <Ionicons name="checkmark-circle-outline" size={28} color="#9ca3af" />
          <Text style={styles.noActiveText}>Sin misiones activas hoy</Text>
        </View>
      )}

      {/* Stats del mes */}
      <View style={styles.statsRow}>
        <StatCard icon="calendar-outline"      value={thisMonth.length}  label="Este mes"    color="#1d4ed8" />
        <StatCard icon="checkmark-done-outline" value={completedMonth.length} label="Completadas" color="#16a34a" />
        <StatCard icon="map-outline"            value={totalHa > 0 ? totalHa.toFixed(1) : '—'} label="Hectáreas" color="#7c3aed" />
      </View>

      {/* Próximas */}
      {missions.filter(m => m.status !== 'completed').length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Próximas misiones</Text>
            <TouchableOpacity onPress={() => navigation.navigate('PilotMissions')}>
              <Text style={styles.seeAll}>Ver todas</Text>
            </TouchableOpacity>
          </View>
          {missions.filter(m => m.status !== 'completed').slice(0, 3).map(m => (
            <MissionCard key={m.id} item={m} onPress={() => goToDetail(m)} />
          ))}
        </View>
      )}

      {/* Historial */}
      {completedMonth.length > 0 && (
        <View style={[styles.section, { marginBottom: 24 }]}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Historial reciente</Text>
            <TouchableOpacity onPress={() => navigation.navigate('PilotMissions')}>
              <Text style={styles.seeAll}>Ver todas</Text>
            </TouchableOpacity>
          </View>
          {completedMonth.slice(0, 2).map(m => (
            <MissionCard key={m.id} item={m} onPress={() => goToDetail(m)} />
          ))}
        </View>
      )}

      {missions.length === 0 && (
        <View style={styles.emptyState}>
          <Ionicons name="airplane-outline" size={52} color="#e5e7eb" />
          <Text style={styles.emptyTitle}>Sin misiones asignadas</Text>
          <Text style={styles.emptyText}>Cuando te asignen una misión aparecerá aquí.</Text>
        </View>
      )}
    </ScrollView>
  );
}

const StatCard = ({ icon, value, label, color }) => (
  <View style={styles.statCard}>
    <View style={[styles.statIcon, { backgroundColor: color + '18' }]}>
      <Ionicons name={icon} size={20} color={color} />
    </View>
    <Text style={[styles.statValue, { color }]}>{value}</Text>
    <Text style={styles.statLabel}>{label}</Text>
  </View>
);

const STATUS_CONFIG = {
  pending:     { color: '#d97706', bg: '#fef3c7', icon: 'time-outline' },
  assigned:    { color: '#d97706', bg: '#fef3c7', icon: 'time-outline' },
  in_progress: { color: '#7c3aed', bg: '#ede9fe', icon: 'airplane-outline' },
  completed:   { color: '#16a34a', bg: '#dcfce7', icon: 'checkmark-circle-outline' },
  cancelled:   { color: '#9ca3af', bg: '#f3f4f6', icon: 'close-circle-outline' },
};

const styles = StyleSheet.create({
  container:       { flex: 1, backgroundColor: '#f5f7fa' },
  centered:        { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header:          { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, paddingBottom: 12 },
  greeting:        { fontSize: 22, fontWeight: '700', color: '#111827' },
  date:            { fontSize: 13, color: '#9ca3af', marginTop: 2 },
  avatarCircle:    { width: 44, height: 44, borderRadius: 22, backgroundColor: '#2c3e50', justifyContent: 'center', alignItems: 'center' },
  avatarText:      { color: '#fff', fontSize: 18, fontWeight: '700' },
  activeCard:      { marginHorizontal: 16, marginBottom: 16, backgroundColor: '#2c3e50', borderRadius: 20, padding: 20 },
  activeCardTop:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  activeBadge:     { backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, paddingHorizontal: 10, paddingVertical: 4 },
  activeBadgeText: { color: '#fff', fontSize: 11, fontWeight: '700', letterSpacing: 0.5 },
  activeTitle:     { color: '#fff', fontSize: 18, fontWeight: '700', marginBottom: 2 },
  activeId:        { color: 'rgba(255,255,255,0.6)', fontSize: 11, marginBottom: 6 },
  activeSub:       { color: 'rgba(255,255,255,0.7)', fontSize: 13, marginBottom: 14 },
  noActiveCard:    { marginHorizontal: 16, marginBottom: 16, backgroundColor: '#fff', borderRadius: 16, padding: 20, flexDirection: 'row', alignItems: 'center', gap: 12, borderWidth: 1, borderColor: '#f3f4f6' },
  noActiveText:    { color: '#9ca3af', fontSize: 14 },
  statsRow:        { flexDirection: 'row', paddingHorizontal: 12, gap: 8, marginBottom: 8 },
  statCard:        { flex: 1, backgroundColor: '#fff', borderRadius: 16, padding: 14, alignItems: 'center', borderWidth: 1, borderColor: '#f3f4f6' },
  statIcon:        { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center', marginBottom: 8 },
  statValue:       { fontSize: 20, fontWeight: '700', marginBottom: 2 },
  statLabel:       { fontSize: 11, color: '#9ca3af', fontWeight: '500' },
  section:         { marginHorizontal: 12, marginTop: 12 },
  sectionHeader:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  sectionTitle:    { fontSize: 15, fontWeight: '700', color: '#111827' },
  seeAll:          { fontSize: 13, color: '#2c3e50', fontWeight: '600' },
  activeWeatherRow:  { flexDirection: 'row', gap: 14, marginTop: 10, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.15)', paddingTop: 12 },
  activeWeatherItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  activeWeatherText: { color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: '500' },
  activeNavBtn:      { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 12, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.15)', paddingTop: 12, alignSelf: 'flex-start' },
  activeNavBtnText:  { color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: '600' },
  miniNavBtn:        { padding: 4 },
  emptyState:      { alignItems: 'center', paddingVertical: 60, paddingHorizontal: 40 },
  emptyTitle:      { fontSize: 16, fontWeight: '600', color: '#374151', marginTop: 16, marginBottom: 8 },
  emptyText:       { fontSize: 13, color: '#9ca3af', textAlign: 'center', lineHeight: 20 },
});
